import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import api, { fmtMoney, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  Loader2,
  Search,
  Users,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function defaultYearRange() {
  const y = new Date().getFullYear();
  return { desde: `${y}-01-01`, hasta: `${y}-12-31` };
}

function estatusBadge(estatus) {
  const isBaja = (estatus || "").toLowerCase() === "baja";
  return (
    <span
      className={cn(
        "inline-flex rounded px-2 py-0.5 text-[10px] font-semibold uppercase",
        isBaja ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800",
      )}
    >
      {isBaja ? "Baja" : "Activo"}
    </span>
  );
}

function matchWorkerSearch(row, needle) {
  const n = (needle || "").trim().toUpperCase();
  if (!n) return true;
  const blob = [row?.nombre_completo, row?.rfc, row?.curp, row?.nss].filter(Boolean).join(" ").toUpperCase();
  return blob.includes(n);
}

function matchHuerfanoSearch(row, needle) {
  const n = (needle || "").trim().toUpperCase();
  if (!n) return true;
  const blob = [row?.rfc, row?.nombre_trabajador].filter(Boolean).join(" ").toUpperCase();
  return blob.includes(n);
}

/**
 * Consulta trabajadores del catálogo con nóminas CFDI del periodo (Control de Nóminas).
 */
export default function ControlNominasConsultaPanel({ clientId, patronales = [] }) {
  const [{ desde, hasta }, setRange] = useState(defaultYearRange);
  const [q, setQ] = useState("");
  const [estatus, setEstatus] = useState("todos");
  const [patronalId, setPatronalId] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [openIds, setOpenIds] = useState({});
  const [huerfanasOpen, setHuerfanasOpen] = useState(false);

  const patronalSelectValue = patronalId || "__all__";

  const resetOpen = useCallback(() => setOpenIds({}), []);

  const loadConsulta = useCallback(async () => {
    if (!clientId) return;
    if (!desde || !hasta) {
      toast.error("Indique el rango de fechas");
      return;
    }
    if (desde > hasta) {
      toast.error("La fecha inicial no puede ser mayor que la final");
      return;
    }
    setBusy(true);
    resetOpen();
    try {
      const { data: res } = await api.get("/laboral/control-nominas/trabajadores", {
        params: {
          client_id: clientId,
          fecha_desde: desde,
          fecha_hasta: hasta,
          estatus_laboral: estatus,
          registro_patronal_id: patronalId || "",
        },
      });
      setData(res);
    } catch (e) {
      setData(null);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo consultar nóminas");
    } finally {
      setBusy(false);
    }
  }, [clientId, desde, hasta, estatus, patronalId, resetOpen]);

  useEffect(() => {
    if (clientId) loadConsulta();
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = data?.summary || {};
  const items = useMemo(() => (Array.isArray(data?.items) ? data.items : []), [data]);
  const huerfanos = useMemo(() => (Array.isArray(data?.nominas_huerfanas) ? data.nominas_huerfanas : []), [data]);

  const filteredItems = useMemo(
    () => items.filter((row) => matchWorkerSearch(row, q)),
    [items, q],
  );
  const filteredHuerfanos = useMemo(
    () => huerfanos.filter((row) => matchHuerfanoSearch(row, q)),
    [huerfanos, q],
  );
  const displaySummary = useMemo(() => {
    const needle = (q || "").trim();
    if (!needle) return summary;
    const activos = filteredItems.filter((i) => i.estatus_laboral !== "baja").length;
    const bajas = filteredItems.filter((i) => i.estatus_laboral === "baja").length;
    const conNomina = filteredItems.filter((i) => (i.num_recibos || 0) > 0).length;
    const sinNomina = filteredItems.filter((i) => i.sin_nomina_periodo).length;
    return {
      ...summary,
      trabajadores_activos: activos,
      trabajadores_baja: bajas,
      con_nomina_en_periodo: conNomina,
      sin_nomina_en_periodo: sinNomina,
    };
  }, [summary, filteredItems, q]);

  const toggleRow = (id) => {
    setOpenIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const downloadGeneral = async () => {
    if (!clientId) return;
    setBusy(true);
    try {
      const url = new URL(`${API}/laboral/control-nominas/reporte/general`, window.location.origin);
      url.searchParams.set("client_id", clientId);
      url.searchParams.set("fecha_desde", desde);
      url.searchParams.set("fecha_hasta", hasta);
      url.searchParams.set("q", q.trim());
      url.searchParams.set("estatus_laboral", estatus);
      if (patronalId) url.searchParams.set("registro_patronal_id", patronalId);
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) throw new Error("Error al generar reporte");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `control-nominas-${desde}-${hasta}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Reporte general descargado");
    } catch {
      toast.error("No se pudo descargar el reporte general");
    } finally {
      setBusy(false);
    }
  };

  const downloadTrabajador = async (trabajadorId, nombre) => {
    if (!clientId || !trabajadorId) return;
    setBusy(true);
    try {
      const url = new URL(`${API}/laboral/control-nominas/reporte/trabajador/${trabajadorId}`, window.location.origin);
      url.searchParams.set("client_id", clientId);
      url.searchParams.set("fecha_desde", desde);
      url.searchParams.set("fecha_hasta", hasta);
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) throw new Error("Error al generar reporte");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `nomina-${(nombre || "trabajador").slice(0, 30)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Reporte del trabajador descargado");
    } catch {
      toast.error("No se pudo descargar el reporte del trabajador");
    } finally {
      setBusy(false);
    }
  };

  const downloadHuerfanoRfc = async (rfc, nombre) => {
    if (!clientId || !rfc) return;
    setBusy(true);
    try {
      const url = new URL(`${API}/laboral/control-nominas/reporte/rfc/${encodeURIComponent(rfc)}`, window.location.origin);
      url.searchParams.set("client_id", clientId);
      url.searchParams.set("fecha_desde", desde);
      url.searchParams.set("fecha_hasta", hasta);
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) throw new Error("Error al generar reporte");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `nomina-sin-catalogo-${(rfc || "rfc").slice(0, 13)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Reporte Excel descargado — ${nombre || rfc}`);
    } catch {
      toast.error("No se pudo descargar el reporte Excel");
    } finally {
      setBusy(false);
    }
  };

  const renderRecibosTable = (recibos) => {
    const list = Array.isArray(recibos) ? recibos : [];
    if (!list.length) {
      return (
        <p className="text-xs text-muted-foreground py-2 m-0">
          Sin recibos de nómina en el periodo seleccionado.
        </p>
      );
    }
    return (
      <div className="overflow-x-auto rounded border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha pago</TableHead>
              <TableHead>Periodo</TableHead>
              <TableHead>Días</TableHead>
              <TableHead>SDI</TableHead>
              <TableHead>Percepciones</TableHead>
              <TableHead>ISR</TableHead>
              <TableHead>Neto</TableHead>
              <TableHead>Origen</TableHead>
              <TableHead>UUID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((rec) => (
              <TableRow key={rec.uuid || `${rec.fecha_pago}-${rec.total_neto}`}>
                <TableCell className="text-xs">{rec.fecha_pago || "—"}</TableCell>
                <TableCell className="text-xs">
                  {rec.fecha_inicial && rec.fecha_final
                    ? `${rec.fecha_inicial} → ${rec.fecha_final}`
                    : "—"}
                </TableCell>
                <TableCell className="text-xs">{rec.num_dias_pagados ?? "—"}</TableCell>
                <TableCell className="text-xs">{rec.salario_diario_integrado || "—"}</TableCell>
                <TableCell className="text-xs">{fmtMoney(rec.total_percepciones)}</TableCell>
                <TableCell className="text-xs">{fmtMoney(rec.isr_retenido)}</TableCell>
                <TableCell className="text-xs font-medium">{fmtMoney(rec.total_neto)}</TableCell>
                <TableCell className="text-xs">
                  {rec.origen === "visor_sat" ? "Visor SAT" : "Import. local"}
                </TableCell>
                <TableCell className="font-mono text-[10px] max-w-[120px] truncate" title={rec.uuid}>
                  {rec.uuid || "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderRecibos = (recibos, trabajadorId, nombre) => {
    const list = Array.isArray(recibos) ? recibos : [];
    if (!list.length) {
      return (
        <p className="text-xs text-muted-foreground py-2 px-4 m-0">
          Sin recibos de nómina en el periodo para este trabajador (revise RFC o importe XML en Control de Nóminas).
        </p>
      );
    }
    return (
      <div className="px-4 pb-3 space-y-2">
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs border-[#0F2D5C] text-[#0F2D5C]"
            disabled={busy}
            onClick={() => downloadTrabajador(trabajadorId, nombre)}
            data-testid={`btn-reporte-nomina-trabajador-${trabajadorId}`}
          >
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
            Excel nóminas del periodo
          </Button>
        </div>
        {renderRecibosTable(recibos)}
      </div>
    );
  };

  const renderHuerfanoDetalle = (h) => {
    const recibos = Array.isArray(h.recibos) ? h.recibos : [];
    const totPerc = recibos.reduce((s, r) => s + Number(r.total_percepciones || 0), 0);
    const totDias = recibos.reduce((s, r) => s + Number(r.num_dias_pagados || 0), 0);
    return (
      <Tabs defaultValue="recibos" className="w-full">
        <TabsList className="h-8">
          <TabsTrigger value="recibos" className="text-xs px-3">
            Recibos de nómina ({recibos.length})
          </TabsTrigger>
          <TabsTrigger value="excel" className="text-xs px-3">
            Reporte Excel
          </TabsTrigger>
        </TabsList>
        <TabsContent value="recibos" className="mt-3">
          {renderRecibosTable(recibos)}
        </TabsContent>
        <TabsContent value="excel" className="mt-3 space-y-3">
          <div className="rounded-md border bg-white p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div>
              <p className="text-muted-foreground m-0">Recibos</p>
              <p className="font-semibold m-0">{h.num_recibos ?? recibos.length}</p>
            </div>
            <div>
              <p className="text-muted-foreground m-0">Días pagados</p>
              <p className="font-semibold m-0">{totDias}</p>
            </div>
            <div>
              <p className="text-muted-foreground m-0">Percepciones</p>
              <p className="font-semibold m-0">{fmtMoney(totPerc)}</p>
            </div>
            <div>
              <p className="text-muted-foreground m-0">Neto periodo</p>
              <p className="font-semibold m-0">{fmtMoney(h.total_neto)}</p>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground m-0">
            Descargue el listado completo de recibos del periodo {desde} a {hasta} con percepciones, ISR, IMSS y UUID.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-[#0F2D5C] text-[#0F2D5C]"
            disabled={busy || !recibos.length}
            onClick={() => downloadHuerfanoRfc(h.rfc, h.nombre_trabajador)}
            data-testid={`btn-reporte-huerfano-${h.rfc}`}
          >
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
            Descargar reporte Excel
          </Button>
          {renderRecibosTable(recibos)}
        </TabsContent>
      </Tabs>
    );
  };

  return (
    <section
      className="rounded-lg border bg-card p-4 md:p-6 space-y-4"
      data-testid="control-nominas-consulta-panel"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4" />
            Consulta y reportes de nóminas
          </h3>
          <p className="text-sm text-muted-foreground mt-1 m-0">
            Trabajadores registrados vinculados a recibos del periodo (Visor SAT o importación local).
            Compare con confronta cuatrimestral y detecte faltantes.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 border-[#0F2D5C] text-[#0F2D5C]"
          disabled={!clientId || busy || !filteredItems.length}
          onClick={downloadGeneral}
          data-testid="btn-reporte-general-control-nominas"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Reporte general Excel
        </Button>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-1 lg:col-span-2">
          <Label className="text-xs">Buscar (nombre, RFC, CURP, NSS)</Label>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                loadConsulta();
              }
            }}
            placeholder="Filtrar trabajadores…"
            disabled={!clientId || busy}
            data-testid="input-buscar-control-nominas"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Desde</Label>
          <Input
            type="date"
            value={desde}
            onChange={(e) => setRange((p) => ({ ...p, desde: e.target.value }))}
            disabled={!clientId || busy}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Hasta</Label>
          <Input
            type="date"
            value={hasta}
            onChange={(e) => setRange((p) => ({ ...p, hasta: e.target.value }))}
            disabled={!clientId || busy}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Estatus</Label>
          <Select value={estatus} onValueChange={setEstatus} disabled={!clientId || busy}>
            <SelectTrigger data-testid="select-estatus-control-nominas">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="activo">Activos</SelectItem>
              <SelectItem value="baja">Dados de baja</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {patronales.length ? (
        <div className="space-y-1 max-w-md">
          <Label className="text-xs">Registro patronal</Label>
          <Select
            value={patronalSelectValue}
            onValueChange={(v) => setPatronalId(v === "__all__" ? "" : v)}
            disabled={!clientId || busy}
          >
            <SelectTrigger>
              <SelectValue placeholder="Todos los patrones" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {patronales.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.registro_patronal} — {p.nombre_sucursal_obra || "Patrón"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <Button
        type="button"
        className="bg-[#0F2D5C] hover:bg-[#0a2247]"
        disabled={!clientId || busy}
        onClick={loadConsulta}
        data-testid="btn-consultar-control-nominas"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
        Consultar nóminas del periodo
      </Button>

      {data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {[
              ["Activos", displaySummary.trabajadores_activos],
              ["Baja", displaySummary.trabajadores_baja],
              ["Con nómina", displaySummary.con_nomina_en_periodo],
              ["Sin nómina", displaySummary.sin_nomina_en_periodo],
              ["Visor SAT", displaySummary.total_recibos_cfdi],
              ["Import. local", displaySummary.total_recibos_laboral],
            ].map(([label, val]) => (
              <div key={label} className="rounded-md border bg-slate-50 px-3 py-2 text-center">
                <p className="text-[10px] uppercase text-slate-500 m-0">{label}</p>
                <p className="text-lg font-semibold text-[#0F2D5C] m-0">{val ?? 0}</p>
              </div>
            ))}
          </div>

          {q.trim() ? (
            <p className="text-xs text-muted-foreground m-0">
              Mostrando {filteredItems.length} trabajador(es) y {filteredHuerfanos.length} sin catálogo
              {" "}de {items.length} / {huerfanos.length} — filtro: «{q.trim()}»
            </p>
          ) : null}

          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Trabajador</TableHead>
                  <TableHead>RFC</TableHead>
                  <TableHead>NSS</TableHead>
                  <TableHead>Estatus</TableHead>
                  <TableHead>Recibos</TableHead>
                  <TableHead>Neto periodo</TableHead>
                  <TableHead>SDI cat.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                      {items.length === 0
                        ? "No hay trabajadores que coincidan con los filtros."
                        : "Ningún trabajador coincide con la búsqueda."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((row) => {
                    const open = Boolean(openIds[row.trabajador_id]);
                    return (
                      <Fragment key={row.trabajador_id}>
                        <TableRow
                          key={row.trabajador_id}
                          className={cn(
                            "cursor-pointer hover:bg-slate-50",
                            row.sin_nomina_periodo && "bg-amber-50/40",
                          )}
                          onClick={() => toggleRow(row.trabajador_id)}
                          data-testid={`fila-control-nomina-${row.trabajador_id}`}
                        >
                          <TableCell>
                            {open ? (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            )}
                          </TableCell>
                          <TableCell className="font-medium text-sm">{row.nombre_completo || "—"}</TableCell>
                          <TableCell className="font-mono text-xs">{row.rfc || "—"}</TableCell>
                          <TableCell className="font-mono text-xs">{row.nss || "—"}</TableCell>
                          <TableCell>{estatusBadge(row.estatus_laboral)}</TableCell>
                          <TableCell>
                            <span className={cn(row.num_recibos ? "text-emerald-700 font-semibold" : "text-amber-700")}>
                              {row.num_recibos ?? 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm">{fmtMoney(row.total_neto)}</TableCell>
                          <TableCell className="text-xs">{row.sdi_catalogo || "—"}</TableCell>
                        </TableRow>
                        {open ? (
                          <TableRow key={`${row.trabajador_id}-detail`} className="bg-slate-50/80 hover:bg-slate-50/80">
                            <TableCell colSpan={8} className="p-0">
                              {renderRecibos(row.recibos, row.trabajador_id, row.nombre_completo)}
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {filteredHuerfanos.length ? (
            <Collapsible
              open={huerfanasOpen}
              onOpenChange={setHuerfanasOpen}
              className="rounded-md border border-amber-200 bg-amber-50/60"
              data-testid="panel-nominas-huerfanas"
            >
              <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 p-3 text-left hover:bg-amber-50/80 rounded-md transition-colors">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-amber-900 m-0 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    Nóminas sin trabajador en catálogo ({filteredHuerfanos.length}
                    {q.trim() && huerfanos.length !== filteredHuerfanos.length
                      ? ` de ${huerfanos.length}`
                      : ""}
                    )
                  </p>
                  <p className="text-[11px] text-amber-800 m-0 mt-0.5">
                    RFC con recibos no registrados en el catálogo. Expanda y seleccione para ver recibos o descargar Excel.
                  </p>
                </div>
                {huerfanasOpen ? (
                  <ChevronDown className="w-4 h-4 text-amber-800 shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-amber-800 shrink-0" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="px-3 pb-3">
                <Accordion type="single" collapsible className="rounded-md border border-amber-200/80 bg-white">
                  {filteredHuerfanos.map((h) => (
                    <AccordionItem key={h.rfc} value={h.rfc} className="border-amber-100 last:border-b-0">
                      <AccordionTrigger
                        className="px-3 py-2 text-xs font-normal hover:bg-amber-50/50"
                        data-testid={`huerfano-trigger-${h.rfc}`}
                      >
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-left">
                          <span className="font-mono font-semibold text-[#0F2D5C]">{h.rfc}</span>
                          <span className="text-slate-700">— {h.nombre_trabajador || "Sin nombre"}</span>
                          <span className="text-amber-800">
                            ({h.num_recibos} recibo(s), {fmtMoney(h.total_neto)})
                          </span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="px-3 pb-3">
                        {renderHuerfanoDetalle(h)}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CollapsibleContent>
            </Collapsible>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
